import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { useSelector } from "react-redux";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

function normalizeDescriptions(description) {
  if (!description) return [];
  if (Array.isArray(description)) {
    return description
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
  }
  const text = String(description).trim();
  return text ? [text] : [];
}

function getDescriptionPreview(description) {
  const entries = normalizeDescriptions(description);
  if (entries.length === 0) return "";
  if (entries.length === 1) return entries[0];
  return entries[entries.length - 1];
}

const SupplierStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const DEFAULT_FILTER_CHECKS = { dateRange: false, createdBy: false };
const DEFAULT_FILTER_INPUTS = { dateFrom: "", dateTo: "", createdBy: "" };
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  dateFrom: "",
  dateTo: "",
  createdBy: "",
};

const MAX_ACTIVE_FILTERS = 2;

const SupplyMaster = () => {
  const navigate = useNavigate();
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("supply_master");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("supplyMasterFilters") || "{}",
  );
  const user = useSelector((state) => state.user);
  const userRole = user?.role || localStorage.getItem("role");
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);
  const [deleteSupplierName, setDeleteSupplierName] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  ); // Input field value
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  ); // Actual search term that triggers API
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt"); // Column to sort by
  const [sortOrder, setSortOrder] = useState("desc"); // Sort order: 'asc' or 'desc'
  const [isSupplierStatusLoading, setIsSupplierStatusLoading] = useState(true);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [selectedSupplierName, setSelectedSupplierName] = useState("");

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState(
    savedFilters.filterChecks || DEFAULT_FILTER_CHECKS,
  );
  const [filterInputs, setFilterInputs] = useState(
    savedFilters.filterInputs || DEFAULT_FILTER_INPUTS,
  );
  const [activeMultiFilters, setActiveMultiFilters] = useState(
    savedFilters.activeMultiFilters || DEFAULT_ACTIVE_MULTI_FILTERS,
  );
  const filterPanelRef = useRef(null);

  const [creators, setCreators] = useState([]);
  const [isCreatorsLoading, setIsCreatorsLoading] = useState(false);

  const anyMultiFilterActive =
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.dateFrom ||
    !!activeMultiFilters.dateTo;

  const isFiltersActive = anyMultiFilterActive;
  const showDateColumn = Boolean(
    activeMultiFilters.dateFrom || activeMultiFilters.dateTo,
  );

  const [supplierStatus, setSupplierStatus] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    inactiveSuppliers: 0,
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const buildMultiFilterFields = useCallback(
    (fields = {}) => {
      if (activeMultiFilters.dateFrom)
        fields.dateFrom = activeMultiFilters.dateFrom;
      if (activeMultiFilters.dateTo) fields.dateTo = activeMultiFilters.dateTo;
      if (activeMultiFilters.createdBy)
        fields.createdBy = activeMultiFilters.createdBy;
      return fields;
    },
    [activeMultiFilters],
  );

  // Fetch suppliers with server-side pagination
  const fetchSuppliers = useCallback(async () => {
    // Get userid from localStorage
    // const userId = localStorage.getItem('id');
    // // if (!userId) {
    // //   toast.error("User session expired. Please login again.", { autoClose: 2000 });
    // //   setTimeout(() => {
    // //     navigate('/');
    // //   }, 2000);
    // //   return;
    // // }
    setIsLoading(true);

    try {
      // Build payload - only include status if user is not admin
      const payload = {
        action: "get-suppliers",
        // userid: userId,
        page: currentPage,
        limit: itemsPerPage,
      };

      // Only add status filter for non-admin users (show only active suppliers)
      if (userRole !== "admin") {
        payload.status = "active";
      }

      buildMultiFilterFields(payload);

      // Add search parameter if activeSearchQuery has a value (not empty)
      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      // Add sorting parameters if sortBy is set
      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Check if request was successful
      if (responseData.success === true && responseData.suppliers) {
        // Remove duplicates based on supplier id
        const uniqueSuppliers = responseData.suppliers.filter(
          (supplier, index, self) =>
            index === self.findIndex((s) => s.id === supplier.id),
        );

        setSuppliers(uniqueSuppliers);
        setFilteredSuppliers(uniqueSuppliers);
        // Use pagination object if available, otherwise fallback to count or suppliers length
        let computedTotalCount = null;
        if (responseData.pagination && responseData.pagination.totalCount) {
          computedTotalCount = responseData.pagination.totalCount;
        } else {
          computedTotalCount = responseData.count || uniqueSuppliers.length;
        }

        setTotalCount(computedTotalCount);
      } else {
        toast.error(responseData.message || "Failed to fetch suppliers", {
          autoClose: 2000,
        });
        setSuppliers([]);
        setFilteredSuppliers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch suppliers error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while fetching suppliers",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setSuppliers([]);
      setFilteredSuppliers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    userRole,
    activeSearchQuery,
    sortBy,
    sortOrder,
    buildMultiFilterFields,
  ]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Calculate pagination (server-side)
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    suppliers.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 4) {
        // Show first pages
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show last pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      filterChecks,
      filterInputs,
      activeMultiFilters,
    };

    sessionStorage.setItem("supplyMasterFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    filterChecks,
    filterInputs,
    activeMultiFilters,
  ]);

  const fetchSupplierStatus = useCallback(async () => {
    setIsSupplierStatusLoading(true);
    try {
      const payload = {
        action: "supplier-count",
      };

      // Apply same checkbox multi-filters as the list API
      buildMultiFilterFields(payload);

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      if (responseData.success) {
        setSupplierStatus(responseData.stats);
      }
    } catch (error) {
      console.error("Supplier stats error:", error);
    } finally {
      setIsSupplierStatusLoading(false);
    }
  }, [buildMultiFilterFields]);

  useEffect(() => {
    fetchSupplierStatus();
  }, [fetchSupplierStatus]);

  const fetchCreators = useCallback(async () => {
    setIsCreatorsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-users",
        limit: 100,
        sortBy: "name",
        sortOrder: "asc",
        status: "active",
      });

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          responseData = response.data;
        }
      }

      if (responseData.success && Array.isArray(responseData.users)) {
        setCreators(
          responseData.users.map((u) => ({ id: u.id, name: u.name })),
        );
      }
    } catch (error) {
      console.error("Fetch users error:", error);
    } finally {
      setIsCreatorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  // Handle search input change - if field becomes empty, automatically reset search
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If user backspaces and field becomes empty, automatically reset search to show all records
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      // Field is now empty and there was an active search, so reset it
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle search trigger - on Enter key or search icon click
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    // If search query is empty (user backspaced all characters), reset the active search
    // This will trigger API call without search parameter to show all records
    if (trimmedQuery === "") {
      // Clear the active search to reset filter
      setActiveSearchQuery("");
      // Reset to first page
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      // Set the active search with trimmed query
      setActiveSearchQuery(trimmedQuery);
      // Reset to first page when searching
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    // Map display column names to API field names
    const columnMap = {
      sno: "id", // S.No maps to id for sorting
      client: "clientname",
      email: "email",
      createdBy: "createdBy",
      status: "status",
    };

    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  // Get sort indicator icon
  const getSortIcon = (column) => {
    const columnMap = {
      sno: "id",
      client: "clientname",
      email: "email",
      createdBy: "createdBy",
      status: "status",
    };
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  // Handle delete button click
  const handleDeleteClick = (supplierId, supplierName) => {
    setDeleteSupplierId(supplierId);
    setDeleteSupplierName(supplierName);
    // Bootstrap modal will be triggered by data-toggle attribute
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!deleteSupplierId) {
      return;
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem("id");
    // if (!currentUserId) {
    //   toast.error("User session expired. Please login again.", { autoClose: 2000 });
    //   setTimeout(() => {
    //     navigate('/');
    //   }, 2000);
    //   return;
    // }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-supplier",
        id: deleteSupplierId,
        currentUserId: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Check if deletion was successful
      if (responseData.success === true) {
        toast.success(responseData.message || "Supplier deleted successfully", {
          autoClose: 2000,
        });

        // Close modal
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }

        // Reset delete state
        setDeleteSupplierId(null);
        setDeleteSupplierName("");

        // Refresh supplier list
        // If current page becomes empty after deletion, go to previous page
        if (suppliers.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchSuppliers();
        }
      } else {
        toast.error(responseData.message || "Failed to delete supplier", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete supplier error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while deleting supplier",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  
  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev; 
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            dateFrom: "",
            dateTo: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            dateFrom: "",
            dateTo: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleDateFromChange = (e) => {
    const newDate = e.target.value;
    setFilterInputs((prev) => ({ ...prev, dateFrom: newDate }));
    setActiveMultiFilters((prev) => ({ ...prev, dateFrom: newDate }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleDateToChange = (e) => {
    const newDate = e.target.value;
    setFilterInputs((prev) => ({ ...prev, dateTo: newDate }));
    setActiveMultiFilters((prev) => ({ ...prev, dateTo: newDate }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const truncateText = (text, maxLength = 18) => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatSupplierDate = (dateValue) => {
    if (!dateValue) return "-";
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString();
  };

  const handleDescriptionClick = (description, supplierName) => {
    setSelectedDescription(description || "No description available");
    setSelectedSupplierName(supplierName || "");
    setShowDescriptionModal(true);
  };

  const handleDescriptionCellClick = (supplier) => {
    const descriptions = normalizeDescriptions(supplier.description);
    const supplierId = supplier.id || supplier._id;

    if (descriptions.length > 1 && supplierId) {
      navigate(`/supplyMasterView/${supplierId}?tab=description`);
      return;
    }

    const preview = descriptions[0] || "";
    if (preview.length > 25) {
      handleDescriptionClick(preview, supplier.clientname);
    }
  };

  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
    setSelectedDescription("");
    setSelectedSupplierName("");
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Supplier Master</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <>
                  <Link to="/supplyMasterAdd" className="btn btn-blue mr-2">
                    Add New
                  </Link>

                  <Link to="/supplyMasterImport" className="btn btn-blue">
                    Import
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isSupplierStatusLoading ? (
                  <>
                    <SupplierStatCardSkeleton />
                    <SupplierStatCardSkeleton />
                    <SupplierStatCardSkeleton />
                  </>
                ) : (
                  <>
                    {/* Total Suppliers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#127aea",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Total Suppliers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#2c7fd8",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {supplierStatus.totalSuppliers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Active Suppliers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#196F3D",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Active Suppliers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#27AE60",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {supplierStatus.activeSuppliers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Inactive Suppliers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#424949",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Inactive Suppliers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#7F8C8D",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {supplierStatus.inactiveSuppliers}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Search / Filter Section */}
              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>

                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search suppliers..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />

                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Filter
                        </label>

                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>

                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            className="procurement-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            {[
                              {
                                key: "dateRange",
                                id: "filterCheckDateRange",
                                label: "Date",
                              },
                              {
                                key: "createdBy",
                                id: "filterCheckCreatedBy",
                                label: "Created User",
                              },
                            ].map((filter) => {
                              const activeCount =
                                Object.values(filterChecks).filter(
                                  Boolean,
                                ).length;

                              return (
                                <div
                                  className="custom-control custom-checkbox mb-2"
                                  key={filter.key}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={filter.id}
                                    checked={filterChecks[filter.key]}
                                    disabled={
                                      !filterChecks[filter.key] &&
                                      activeCount >= MAX_ACTIVE_FILTERS
                                    }
                                    onChange={() =>
                                      handleFilterCheckToggle(filter.key)
                                    }
                                  />

                                  <label
                                    className="custom-control-label"
                                    htmlFor={filter.id}
                                  >
                                    {filter.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date From */}
                    {filterChecks.dateRange && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Date From
                          </label>

                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.dateFrom}
                            max={filterInputs.dateTo || undefined}
                            onChange={handleDateFromChange}
                          />
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Date To
                          </label>

                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.dateTo}
                            min={filterInputs.dateFrom || undefined}
                            onChange={handleDateToChange}
                          />
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            Created User
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isCreatorsLoading && creators.length === 0
                                ? "Loading users..."
                                : "Select User"}
                            </option>

                            {creators.map((creator) => (
                              <option key={creator.id} value={creator.id}>
                                {creator.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {anyMultiFilterActive && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{
                            minWidth: "75px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : suppliers.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  {isFiltersActive
                    ? "No suppliers found for the selected filter."
                    : "There are no suppliers yet."}
                  <br />
                  <div className="mt-3">
                    {isFiltersActive ? (
                      <button
                        className="btn btn-blue"
                        onClick={handleClearFilter}
                      >
                        Clear Filter
                      </button>
                    ) : (
                      canCreate && (
                        <Link to="/supplyMasterAdd" className="btn btn-blue">
                          Add New Supplier
                        </Link>
                      )
                    )}
                  </div>
                </div>
              ) : filteredSuppliers.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No suppliers found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="suppliers"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            className="crsr_pntr"
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              S.No
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("client")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              width: "150px",
                              minWidth: "150px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Supplier Name {getSortIcon("client")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("email")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              width: "50px",
                              minWidth: "50px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Email {getSortIcon("email")}
                            </span>
                          </th>
                          <th style={{ width: "120px", minWidth: "120px" }}>
                            Phone
                          </th>
                          <th style={{ width: "100px", minWidth: "100px" }}>
                            Contact Name
                          </th>
                          <th style={{ width: "120px", minWidth: "120px" }}>
                            City
                          </th>
                          {/* <th>State</th> */}
                          <th
                            style={{
                              width: "200px",
                              minWidth: "200px",
                              maxWidth: "200px",
                            }}
                          >
                            Description
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("createdBy")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              width: "120px",
                              minWidth: "120px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Created By {getSortIcon("createdBy")}
                            </span>
                          </th>
                          {showDateColumn && (
                            <th
                              className="crsr_pntr"
                              onClick={() => handleSort("createdAt")}
                              style={{
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                width: "120px",
                                minWidth: "120px",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                Created Date {getSortIcon("createdAt")}
                              </span>
                            </th>
                          )}
                          {userRole === "admin" && (
                            <th
                              className="crsr_pntr"
                              onClick={() => handleSort("status")}
                              style={{
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                width: "80px",
                                minWidth: "80px",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                Status {getSortIcon("status")}
                              </span>
                            </th>
                          )}
                          <th style={{ width: "100px", minWidth: "100px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSuppliers.map((supplier, index) => {
                          const hasAttachments =
                            (supplier.attachmentUrls &&
                              Array.isArray(supplier.attachmentUrls) &&
                              supplier.attachmentUrls.length > 0) ||
                            (supplier.attachmentUrl &&
                              supplier.attachmentUrl.trim() !== "");
                          const descriptions = normalizeDescriptions(
                            supplier.description,
                          );
                          const descriptionPreview = getDescriptionPreview(
                            supplier.description,
                          );
                          const hasMultipleDescriptions =
                            descriptions.length > 1;
                          const showDescriptionLink =
                            hasMultipleDescriptions ||
                            descriptionPreview.length > 25;
                          return (
                            <tr key={supplier.id}>
                              <td style={{ width: "10px", minWidth: "10px" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ width: "150px", minWidth: "150px" }}>
                                {supplier.clientname}
                              </td>
                              <td
                                style={{
                                  width: "150px",
                                  minWidth: "150px",
                                  maxWidth: "150px",
                                  wordWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {supplier.email}
                              </td>
                              <td style={{ width: "120px", minWidth: "120px" }}>
                                {supplier.phonenumber}
                              </td>
                              <td style={{ width: "150px", minWidth: "150px" }}>
                                <div
                                  style={{
                                    width: "90px",
                                    minWidth: "90px",
                                    maxWidth: "90px",
                                    wordWrap: "break-word",
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {supplier.contactPerson || "-"}
                                </div>
                              </td>
                              <td style={{ width: "120px", minWidth: "120px" }}>
                                {supplier.city}
                              </td>
                              {/* <td>{supplier.state}</td> */}
                              <td
                                style={{
                                  width: "200px",
                                  minWidth: "200px",
                                  maxWidth: "200px",
                                  fontSize: "12px",
                                  cursor: showDescriptionLink
                                    ? "pointer"
                                    : "default",
                                }}
                                onClick={() =>
                                  handleDescriptionCellClick(supplier)
                                }
                                title={descriptionPreview || "-"}
                              >
                                {descriptionPreview ? (
                                  <>
                                    {truncateText(descriptionPreview, 25)}
                                    {showDescriptionLink && (
                                      <i
                                        className="fa-solid fa-external-link-alt ml-1"
                                        style={{ fontSize: "10px" }}
                                      />
                                    )}
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td style={{ width: "120px", minWidth: "120px" }}>
                                {supplier.createdBy || "-"}
                              </td>
                              {showDateColumn && (
                                <td
                                  style={{ width: "120px", minWidth: "120px" }}
                                >
                                  {formatSupplierDate(supplier.createdAt)}
                                </td>
                              )}
                              {userRole === "admin" && (
                                <td style={{ width: "80px", minWidth: "80px" }}>
                                  <span
                                    className={`fwt_600 ${supplier.status === "active" ? "f_green" : "f_red"}`}
                                    style={{ fontSize: "90%", fontWeight: 550 }}
                                  >
                                    {supplier.status
                                      ? supplier.status
                                          .charAt(0)
                                          .toUpperCase() +
                                        supplier.status.slice(1).toLowerCase()
                                      : "-"}
                                  </span>
                                </td>
                              )}
                              <td
                                style={{
                                  width: "100px",
                                  minWidth: "100px",
                                  maxWidth: "100px",
                                  wordWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {canView && (
                                    <Link
                                      to={`/supplyMasterView/${supplier.id}`}
                                      className="crsr_pntr"
                                    >
                                      <i className="fa-solid fa-eye mr-2" />
                                    </Link>
                                  )}
                                  {canUpdate && (
                                    <Link
                                      to={`/supplyMasterEdit/${supplier.id}`}
                                      className="crsr_pntr"
                                      style={{ display: "inline-block" }}
                                    >
                                      <i className="fa-solid fa-pen mr-2" />
                                    </Link>
                                  )}

                                  {canDelete && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() =>
                                        handleDeleteClick(
                                          supplier.id,
                                          supplier.clientname,
                                        )
                                      }
                                    >
                                      <i className="fa-solid fa-trash-can mr-2" />
                                    </a>
                                  )}

                                  {hasAttachments && (
                                    <span
                                      className="crsr_pntr"
                                      style={{ display: "inline-block" }}
                                    >
                                      <i
                                        className="fa-solid fa-paperclip  mr-2"
                                        style={{ fontSize: "1.1em" }}
                                        title={
                                          supplier.attachmentUrls &&
                                          Array.isArray(supplier.attachmentUrls)
                                            ? `${supplier.attachmentUrls.length} attachment(s)`
                                            : "1 attachment"
                                        }
                                      />
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {!isLoading && suppliers.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Description Modal */}
      <div
        className={`modal fade ${showDescriptionModal ? "show" : ""}`}
        style={{ display: showDescriptionModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="supplierDescriptionModalLabel"
        aria-hidden={!showDescriptionModal}
        onClick={handleCloseDescriptionModal}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div
              className="modal-header"
              style={{ backgroundColor: "#17a2b8", color: "white" }}
            >
              <h5 className="modal-title" id="supplierDescriptionModalLabel">
                <i className="fa-solid fa-file-lines mr-2" />
                Description{" "}
                {selectedSupplierName && `- ${selectedSupplierName}`}
              </h5>
              <button
                type="button"
                className="close"
                onClick={handleCloseDescriptionModal}
                aria-label="Close"
                style={{ color: "white" }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ padding: "20px", fontSize: "13px", lineHeight: "1.6" }}
            >
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "15px",
                  borderRadius: "5px",
                  border: "1px solid #dee2e6",
                  wordWrap: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedDescription}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDescriptionModal && (
        <div
          className="modal-backdrop fade show"
          onClick={handleCloseDescriptionModal}
        />
      )}

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Supplier
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteSupplierName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplyMaster;
